import React, { useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";

import {
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Box,
  Avatar,
  Typography,
  Fade,
  Modal,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LockIcon from "@mui/icons-material/Lock";
import LoginIcon from "@mui/icons-material/Login";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CircleIcon from "@mui/icons-material/Circle";
import CloseIcon from "@mui/icons-material/Close";

import { login } from "../api/authApi";

const Login = () => {
  const navigate = useNavigate();
  const { setUser } = useContext(UserContext);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  const slides = [
    {
      img: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80",
      title: "PREMIUM SERVICES",
      desc: "Experience the most secure node management in the industry.",
    },
    {
      img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80",
      title: "LIVE NODE TELEMETRY",
      desc: "Real-time monitoring of every branch gateway across the global network.",
    },
    {
      img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80&sat=-100&bri=40",
      title: "STALE FAULT DETECTION",
      desc: "Automated isolation of unresolved branch failures exceeding 24-hour thresholds.",
    },
    {
      img: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=1200&q=80",
      title: "DATA ANALYTICS",
      desc: "Deep-packet inspection and statistical modeling of branch performance.",
    },
    {
      img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&q=80",
      title: "ENCRYPTED TOPOLOGY",
      desc: "Secure end-to-end visibility from the local CPE to the central server gateway.",
    },
    {
      img: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=1200&q=80",
      title: "MULTI-PLATFORM",
      desc: "Seamlessly manage your entire branch network from any terminal.",
    },
  ];

  const nextSlide = () =>
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () =>
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login({ username, password });
      
      if (res.error) {
        toast.error(res.error);
        setLoading(false);
        return;
      }
      
      if (!res.token) {
        toast.error("No token received from server");
        setLoading(false);
        return;
      }
      
      localStorage.setItem("token", res.token);
      
      const userData = {
        name: res.name,
        username: res.username,
        role: res.role,
        token: res.token,
        company: res.company,
        mustChangePassword: Boolean(res.mustChangePassword),
      };
        
      setUser(userData);

      if (res.mustChangePassword) {
        toast.info("Please set a new password before continuing.");
        navigate("/change-password", { replace: true });
        setModalOpen(false);
        return;
      }

      toast.success(`Welcome ${userData.name || userData.username}!`);

      const routes = {
        BEN_SUPER: "/benSuper",
        BEN_ADMIN: "/benAdmin",
        TELE_SUPER: "/teleSuper",
        TELE_ADMIN: "/teleAdmin",
        COMPANY_SUPER: "/companySuper",
        COMPANY_ADMIN: "/companyAdmin",
        END_USER: "/branch",
        TELE_USER: "/teleUser",
      };

      const targetRoute = routes[res.role] || "/";
      navigate(targetRoute, { replace: true });
      setModalOpen(false);
      
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.message || "Auth System Failure");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        minHeight: "100vh",
        backgroundColor: "#FFFFFF",
      }}
    >
      {/* Slider - Fullscreen on mobile, 60% on desktop */}
      <Box
        sx={{
          width: { xs: "100%", md: "60%" },
          height: { xs: "100vh", md: "auto" },
          backgroundColor: "#FFFFFF",
          position: "relative",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          display: "flex",
        }}
      >
        {slides.map((slide, index) => (
          <Fade in={currentSlide === index} key={index} timeout={800}>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${slide.img})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          </Fade>
        ))}

        {/* Navigation arrows */}
        <IconButton
          onClick={prevSlide}
          sx={{
            position: "absolute",
            left: { xs: 8, md: 20 },
            top: "50%",
            transform: "translateY(-50%)",
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.5)",
            "&:hover": { bgcolor: "#FFD700", color: "#000" },
            zIndex: 10,
          }}
        >
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
        <IconButton
          onClick={nextSlide}
          sx={{
            position: "absolute",
            right: { xs: 8, md: 20 },
            top: "50%",
            transform: "translateY(-50%)",
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.5)",
            "&:hover": { bgcolor: "#FFD700", color: "#000" },
            zIndex: 10,
          }}
        >
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>

        {/* Slide Content - positioned near top on mobile */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            maxWidth: 600,
            p: { xs: 2, md: 6 },
            pt: { xs: 8, md: 6 },
            borderRadius: { xs: "16px", md: "24px" },
            backgroundColor: "rgba(255,255,255,0.85)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255, 255, 255, 0.5)",
            boxShadow: "0 15px 35px rgba(0,0,0,0.1)",
            textAlign: "center",
            mx: { xs: 2, md: 0 },
            mt: { xs: -4, md: 0 },
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: { xs: "#333", md: "#888" },
              fontWeight: 800,
              letterSpacing: { xs: 4, md: 6 },
              display: "block",
              mb: 1,
              fontSize: { xs: "0.6rem", md: "0.9rem" },
            }}
          >
            NETDESK SYSTEMS
          </Typography>

          <Typography
            variant="h2"
            fontWeight={900}
            sx={{
              color: "#111",
              mb: 1.5,
              textTransform: "uppercase",
              lineHeight: 1.1,
              fontSize: { xs: "1.4rem", md: "2.8rem" },
            }}
          >
            {slides[currentSlide].title}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "#333",
              mb: { xs: 2.5, md: 4 },
              fontWeight: 500,
              lineHeight: 1.4,
              fontSize: { xs: "0.75rem", md: "1rem" },
            }}
          >
            {slides[currentSlide].desc}
          </Typography>

          {/* Dot indicators */}
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", mb: { xs: 2.5, md: 0 } }}>
            {slides.map((_, index) => (
              <CircleIcon
                key={index}
                onClick={() => setCurrentSlide(index)}
                sx={{
                  fontSize: { xs: 7, md: 10 },
                  cursor: "pointer",
                  color: currentSlide === index ? "#FFD700" : "rgba(0,0,0,0.2)",
                  transition: "0.3s",
                  "&:hover": { color: "#FFD700" },
                }}
              />
            ))}
          </Box>

          {/* SIGN IN BUTTON - Only visible on mobile */}
          <Button
            variant="contained"
            fullWidth
            onClick={() => setModalOpen(true)}
            endIcon={<LoginIcon />}
            sx={{
              display: { xs: "flex", md: "none" },
              mt: 1.5,
              py: 1.5,
              borderRadius: "50px",
              fontSize: "0.9rem",
              fontWeight: "900",
              color: "#000",
              backgroundColor: "#FFD600",
              boxShadow: "0 8px 20px rgba(255, 215, 0, 0.4)",
              "&:hover": {
                backgroundColor: "#e6c400",
              },
            }}
          >
            SIGN IN
          </Button>
        </Box>
      </Box>

      {/* Login Form - Desktop */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          width: "40%",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#FFFFFF",
          p: { md: 6 },
        }}
      >
        <Fade in timeout={1000}>
          <Box sx={{ width: "100%", maxWidth: 360, textAlign: "center" }}>
            <Avatar
              src="/logo.png"
              sx={{
                width: 80,
                height: 80,
                margin: "0 auto 32px",
                border: "4px solid #FFD600",
                background: "#FFD600",
                color: "#000000",
              }}
            />
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{ color: "#000", mb: 1, letterSpacing: -1, fontSize: { xs: "1.6rem", md: "2rem" } }}
            >
              SIGN IN
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#BBB", fontWeight: 700, mb: 6, letterSpacing: 1, fontSize: { xs: "0.75rem", md: "0.85rem" } }}
            >
              ENTER YOUR CREDENTIALS
            </Typography>

            <form onSubmit={handleLogin}>
              {/* Username Field - Rectangular */}
              <TextField
                fullWidth
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase())}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountCircleIcon sx={{ color: "#000" }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputTheme}
              />

              {/* Password Field - Rectangular */}
              <TextField
                fullWidth
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: "#000" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? (
                          <VisibilityOffIcon sx={{ color: "#AAA" }} />
                        ) : (
                          <VisibilityIcon sx={{ color: "#AAA" }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={inputTheme}
              />

              {/* Login Button - Short and Right Aligned */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  endIcon={<LoginIcon />}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: "50px",
                    fontSize: { xs: "0.85rem", md: "0.9rem" },
                    fontWeight: "900",
                    color: "#000",
                    backgroundColor: "#ffd900d8",
                    boxShadow: "0 10px 25px rgba(255, 215, 0, 0.3)",
                    minWidth: "120px",
                    "&:hover": {
                      backgroundColor: "#e6c400d8",
                      boxShadow: "none",
                    },
                  }}
                >
                  {loading ? "VERIFYING..." : "LOGIN"}
                </Button>
              </Box>
            </form>
          </Box>
        </Fade>
      </Box>

      {/* Mobile Login Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          mt: { xs: 8, sm: 12 },
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "90%",
            maxWidth: 400,
            height: "70vh",
            bgcolor: "#FFFFFF",
            borderRadius: 4,
            boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
            p: 5,
            outline: "none",
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={() => setModalOpen(false)}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "#666",
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Avatar
              src="/logo.png"
              sx={{
                width: 60,
                height: 60,
                margin: "0 auto 12px",
                border: "3px solid #FFD600",
                background: "#FFD600",
                color: "#000000",
              }}
            />
            <Typography
              variant="h5"
              fontWeight={900}
              sx={{ color: "#000", mb: 0.5, letterSpacing: -0.5 }}
            >
              SIGN IN
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#BBB", fontWeight: 700, mb: 3, display: "block", letterSpacing: 1 }}
            >
              ENTER YOUR CREDENTIALS
            </Typography>

            <form onSubmit={(e) => { handleLogin(e); }}>
              <TextField
                fullWidth
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase())}
                margin="dense"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountCircleIcon sx={{ color: "#000", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={mobileInputTheme}
              />

              <TextField
                fullWidth
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="dense"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: "#000", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? (
                          <VisibilityOffIcon sx={{ color: "#AAA", fontSize: 18 }} />
                        ) : (
                          <VisibilityIcon sx={{ color: "#AAA", fontSize: 18 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={mobileInputTheme}
              />

              {/* Mobile Login Button - Short and Right Aligned */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  endIcon={<LoginIcon />}
                  sx={{
                    py: 1.2,
                    px: 3.5,
                    borderRadius: "50px",
                    fontSize: "0.85rem",
                    fontWeight: "900",
                    color: "#000",
                    backgroundColor: "#ffd900d8",
                    boxShadow: "0 4px 12px rgba(255, 215, 0, 0.3)",
                    minWidth: "100px",
                    "&:hover": {
                      backgroundColor: "#e6c400d8",
                    },
                  }}
                >
                  {loading ? "VERIFYING..." : "Login"}
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

// Rectangular Text Fields - No border-radius
const inputTheme = {
  "& .MuiOutlinedInput-root": {
    color: "#000",
    backgroundColor: "#FDFDFD",
    borderRadius: "0px", // Rectangular
    fontWeight: "bold",
    "& fieldset": { borderColor: "#EEE" },
    "&:hover fieldset": { borderColor: "#FFD700" },
    "&.Mui-focused fieldset": { borderColor: "#FFD700", borderWidth: "2px" },
  },
};

const mobileInputTheme = {
  "& .MuiOutlinedInput-root": {
    color: "#000",
    backgroundColor: "#FDFDFD",
    borderRadius: "0px", // Rectangular
    fontWeight: "bold",
    fontSize: "0.9rem",
    "& fieldset": { borderColor: "#EEE" },
    "&:hover fieldset": { borderColor: "#FFD700" },
    "&.Mui-focused fieldset": { borderColor: "#FFD700", borderWidth: "2px" },
  },
};

export default Login;